<script>
import DocumentPanelNavItem from '@/V2/documents/DocumentPanelNavItem';
import DocumentTrustScore from '@/V2/documents/document-view/DocumentTrustScore.vue';
import DocumentSimilarDocuments from '@/V2/documents/document-view/DocumentSimilarDocuments.vue';
import DocumentReviewAgentReport from '@/V2/documents/document-view/document-review-agent/DocumentReviewAgentReport.vue';
import DocumentReviewAgent from '@/V2/documents/document-view/document-review-agent/DocumentReviewAgent.vue';
import * as fileTypes from '@/constants/fileTypes';
import ChecksList from '@/V2/documents/document-view/checks/ChecksList';
import SignalsList from '@/V2/documents/document-view/signals/SignalsList';
import VerificationsList from '@/V2/documents/document-view/verifications/VerificationsList';
import {
  iAccordion,
  iAccordionItem,
  iAccordionItemSizes,
  iIcon,
  iIconButton,
  iTooltip,
  iTooltipAlignmentMode,
} from '@inscribe/signal';
import CheckDetails from '@/V2/documents/document-view/checks/CheckDetails.vue';
import SignalDetails from '@/V2/documents/document-view/signals/SignalDetails.vue';
import VerificationDetails from '@/V2/documents/document-view/verifications/VerificationDetails.vue';
import TrustSignalsList from '@/V2/documents/document-view/signals/TrustSignalsList.vue';
import palette from '@inscribe/signal/dist/palette';
import { mapGetters } from 'vuex';
import DocumentStatusHistory from '@/V2/documents/document-status/DocumentStatusHistory.vue';

export default {
  name: 'DocumentRightPanel',
  components: {
    CheckDetails,
    ChecksList,
    'document-panel-nav-item': DocumentPanelNavItem,
    DocumentSimilarDocuments,
    DocumentTrustScore,
    DocumentReviewAgent,
    DocumentReviewAgentReport,
    DocumentStatusHistory,
    iAccordion,
    iAccordionItem,
    iIcon,
    iIconButton,
    iTooltip,
    SignalDetails,
    SignalsList,
    TrustSignalsList,
    VerificationDetails,
    VerificationsList,
  },
  props: {
    document: {
      type: Object,
      default: null,
    },
    customerId: {
      type: String,
      default: null,
    },
    config: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      fileTypes,
      iAccordionItemSizes,
      iTooltipAlignmentMode,
      overlay: '',
      overlayParam: null,
      toggleRotated: false,
      lastNavItem: null,
      palette,
    };
  },
  computed: {
    ...mapGetters({
      trustSignalsEnabled: 'trustSignalsEnabled',
      isParsedDetailHighlighterEnabled: 'parsedDetailHighlighterEnabled',
      canViewDocumentReviewAgent: 'canViewDocumentReviewAgent',
    }),
    isDocHighlighterEnabled() {
      this.setLastNaveItem();
      return (
        !this.documentIsAnImage &&
        this.$store.state.featureFlagsStore.featureFlags[
          'detector-font-highlighter-tool'
        ]
      );
    },
    documentIsAnImage() {
      return this.document.file_type === fileTypes.IMAGE;
    },
    isFontPageEnabled() {
      this.setLastNaveItem();
      return !this.documentIsAnImage;
    },
    isDocDiffEnabled() {
      this.setLastNaveItem();
      return (
        this.$store.state.currentUser.is_superuser ||
        this.$store.state.featureFlagsStore.featureFlags['doc-diff-enabled']
      );
    },
  },
  watch: {
    config: {
      immediate: true,
      handler(config) {
        if (config?.panel) {
          this.overlay = config.panel;
        } else {
          this.overlay = '';
        }
      },
    },
  },
  methods: {
    openOverlay(overlayKey) {
      this.overlay = overlayKey;
      setTimeout(() => {
        const scrollContainer = this.$refs.scrollableElement;
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }

        // remove scroll if content is smaller
        const overlay = this.$refs.overlay;
        const overlayContent = this.$refs.overlayContent;
        const rightPanel = this.$refs.rightPanel;
        const overlayRect = overlayContent.getBoundingClientRect();
        const panelRect = rightPanel.getBoundingClientRect();
        overlay.style.overflowY =
          overlayRect.height < panelRect.height ? 'hidden' : 'auto';
      });
    },
    onShowDocumentReviewAgentReport() {
      this.openOverlay('document-review-agent-report');
    },
    onShowSimilarDocuments() {
      this.openOverlay('similar-documents');
    },
    onOpenCheckDetails(check) {
      this.openOverlay('check-details');
      this.overlayParam = check;
    },
    onOpenVerificationDetails(verification) {
      this.openOverlay('verification-details');
      this.overlayParam = verification;
    },
    onOpenSignalDetails(signalGroup) {
      this.openOverlay('signal-details');
      this.overlayParam = signalGroup;
    },
    onOpenTrustSignalDetails(signalGroup) {
      this.openOverlay('trust-signal-details');
      this.overlayParam = signalGroup;
    },
    closeOverlay() {
      this.$emit('close-overlay', this.overlay);
      this.overlay = '';
      this.overlayParam = null;
      const scrollContainer = this.$refs.scrollableElement;
      scrollContainer.style.overflowY = 'auto';
    },
    togglePanel(side) {
      this.$emit('toggle-panel', 'right');
      this.toggleRotated = !this.toggleRotated;
    },
    setLastNaveItem() {
      const allNavs = document.querySelectorAll('.document-panel-nav-item');
      const lastNav = allNavs[allNavs.length - 1];
      this.lastNavItem = lastNav ? lastNav.id.replace('nav-item-', '') : '';
    },
  },
};
</script>

<template>
  <div
    ref="rightPanel"
    :class="{ 'overlay-open': overlay !== '' }"
    class="right-panel d-flex flex-column align-items-start"
    data-testid="document-right-panel"
  >
    <i-icon-button
      :class="{ rotated: toggleRotated }"
      class="open-toggle"
      symbol="medium-layout-column-right-hide"
      size="md"
      type="transparent"
      :symbol-stroke="1.5"
      tooltip="Toggle panel"
      tooltip-container="information-requests"
      tooltip-placement="left"
      @button-click="togglePanel()"
    ></i-icon-button>
    <!-- Nav tabs -->
    <div class="document-panel-nav d-flex flex-row justify-content-between">
      <document-panel-nav-item
        icon="circle-exclamation"
        name="document-signals"
        tooltip="Signals"
      />
      <document-panel-nav-item
        icon="font"
        name="document-text"
        tooltip="Text"
      />
      <document-panel-nav-item
        :is-last="lastNavItem === 'document-metadata'"
        icon="list"
        name="document-metadata"
        tooltip="Metadata"
      />
      <document-panel-nav-item
        v-if="isFontPageEnabled"
        :is-last="lastNavItem === 'document-fonts'"
        icon="book"
        name="document-fonts"
        tooltip="Document fonts"
      />
      <document-panel-nav-item
        v-if="isDocDiffEnabled"
        :is-last="lastNavItem === 'document-compare'"
        icon="copy"
        name="document-compare"
        tooltip="Compare"
      />
      <document-panel-nav-item
        v-if="isDocHighlighterEnabled"
        :is-last="lastNavItem === 'document-highlighter'"
        icon="highlighter"
        name="document-highlighter"
        tooltip="Highlight fonts"
      />
      <document-panel-nav-item
        v-if="isParsedDetailHighlighterEnabled"
        :is-last="lastNavItem === 'document-parsed-details-highlighter'"
        icon="box"
        name="document-parsed-details-highlighter"
        tooltip="Highlight parsed details"
      />
    </div>

    <!-- details -->
    <div ref="scrollableElement" class="document-details">
      <!-- Document Review Agent -->
      <document-review-agent
        v-if="canViewDocumentReviewAgent"
        @show-document-review-agent-report="onShowDocumentReviewAgentReport"
      ></document-review-agent>
      <i-accordion>
        <!-- Trust score -->
        <i-accordion-item
          :mount-open="true"
          title="Trust Score"
          :no-background="true"
          :size="iAccordionItemSizes.LARGE"
        >
          <!--title extra content-->
          <div
            slot="extra-title-content"
            class="extra-title-content d-flex flex-row align-items-center i-gap-4"
          >
            <!-- info icon -->
            <i-icon
              symbol="small-question-mark"
              :size="12"
              :colour="palette['light-neutral-80']"
              class="trust-score-help-icon"
              :stroke-width="1.5"
            ></i-icon>
            <i-tooltip
              :target="`.trust-score-help-icon`"
              title="Trust Score"
              description="A document's Trust Score reflects its likelihood of being fraudulent. A higher score means a lower likelihood of fraud, while a lower score indicates a higher likelihood of fraud."
              link-text="Learn more"
              link-external-url="https://help.inscribe.ai/en/articles/5557654-trust-score"
              :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
            />
          </div>
          <document-trust-score
            :customer-id="customerId"
            :document="document"
            class="trust-score-container"
            @show-similar-documents="onShowSimilarDocuments"
          ></document-trust-score>
        </i-accordion-item>
      </i-accordion>

      <!-- Signals List -->
      <signals-list
        :document="document"
        @open-signal-details="onOpenSignalDetails"
      />

      <!-- Trust Signals List -->
      <trust-signals-list
        v-if="trustSignalsEnabled"
        :document="document"
        @open-signal-details="onOpenTrustSignalDetails"
      ></trust-signals-list>

      <!-- Verifications List -->
      <verifications-list
        :document="document"
        @open-verification-details="onOpenVerificationDetails"
      />

      <!-- Checks List -->
      <checks-list
        :document="document"
        @open-check-details="onOpenCheckDetails"
      />
    </div>

    <!-- overlay -->
    <div ref="overlay" class="overlay">
      <i-icon-button
        class="close-icon"
        symbol="medium-delete-1"
        size="md"
        type="transparent"
        tooltip-placement="top"
        @button-click="closeOverlay()"
      ></i-icon-button>
      <div ref="overlayContent" class="content">
        <document-review-agent-report
          v-if="overlay === 'document-review-agent-report'"
        ></document-review-agent-report>
        <document-similar-documents
          v-if="overlay === 'similar-documents'"
          :customer-id="customerId"
          :document="document"
        ></document-similar-documents>

        <check-details
          v-if="overlay === 'check-details' && overlayParam"
          :check="overlayParam"
        ></check-details>

        <verification-details
          v-if="overlay === 'verification-details' && overlayParam"
          :verification="overlayParam"
        ></verification-details>

        <signal-details
          v-if="overlay === 'signal-details' && overlayParam"
          :signal-group="overlayParam"
        ></signal-details>

        <document-status-history
          v-if="overlay === 'history'"
          class="document-status-history"
          :document="document"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.right-panel {
  background-color: $light-neutral-default;
  position: relative;
  width: 100%;

  .open-toggle {
    position: absolute;
    background: $light-neutral-default;
    border-radius: 8px;
    border: 1px solid $light-neutral-60;
    bottom: 16px;
    left: -58px;

    &.rotated {
      transform: rotate(180deg);
    }
  }

  &.overlay-open {
    .i-accordion {
      visibility: hidden;
    }

    .overlay {
      right: 0;

      .close-icon {
        display: flex;
      }
    }
  }

  .document-panel-nav {
    border-bottom: 1px solid $light-neutral-60;
    width: 100%;
  }

  .i-accordion {
    visibility: visible;
    transition: visibility 0.3s ease-out;
  }

  .document-details {
    height: 100%;
    min-width: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    padding-bottom: 24px;
    position: relative;
  }

  .overlay {
    position: absolute;
    background-color: $light-neutral-default;
    top: 0px;
    right: -100%;
    width: 100%;
    bottom: 0;
    transition: right 0.3s ease;

    .close-icon {
      display: none;
      position: absolute;
      right: 24px;
      top: 24px;
    }
  }
}
</style>
